import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  GraduationCap, 
  Plus, 
  Trash2, 
  Search,
  BookOpen,
  Mail,
  UserPlus,
  UserCheck,
  UserMinus,
  Calendar,
  MapPin,
  Navigation,
  Edit,
  Save,
  Target,
  AlertTriangle,
  Heart
} from "lucide-react";
import { ExportDropdown } from "../components/ui/export-dropdown";

const PROGRAMS = [
  "Physical Therapy",
  "Occupational Therapy",
  "Speech Language Pathology",
  "Nursing",
  "Radiology Technology",
  "Medical Laboratory Science",
  "Respiratory Therapy",
  "Health Administration",
];

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export default function Students() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [slots, setSlots] = useState([]);
  const [availableRotations, setAvailableRotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [hardshipFilter, setHardshipFilter] = useState("");
  const [myStudentsFilter, setMyStudentsFilter] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [maxDistance, setMaxDistance] = useState("");
  const [loadingRotations, setLoadingRotations] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    university: "",
    program: "",
    graduation_month: "",
    graduation_year: "",
    preferred_specialty: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    is_hardship: false,
    hardship_notes: "",
  });
  const [editFormData, setEditFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    university: "",
    program: "",
    graduation_month: "",
    graduation_year: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    is_hardship: false,
    hardship_notes: "",
  });
  const [geocoding, setGeocoding] = useState(false);
  const [editGeocoding, setEditGeocoding] = useState(false);

  useEffect(() => {
    fetchData();
  }, [myStudentsFilter]);

  const fetchData = async () => {
    try {
      const params = myStudentsFilter ? { my_students: true } : {};
      const [studentsRes, rotationsRes] = await Promise.all([
        api.get("/students", { params }),
        api.get("/slots")
      ]);
      setStudents(studentsRes.data);
      setSlots(rotationsRes.data.filter(s => !s.is_locked && s.capacity > s.filled));
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignToMe = async (studentId) => {
    try {
      await api.post(`/students/${studentId}/assign`);
      toast.success("Student assigned to you");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign student");
    }
  };

  const handleUnassignFromMe = async (studentId) => {
    try {
      await api.delete(`/students/${studentId}/assign`);
      toast.success("Student unassigned");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unassign student");
    }
  };

  const fetchRotationsForStudent = async (student, maxDist = null) => {
    setLoadingRotations(true);
    try {
      let url = `/slots/for-student/${student.id}`;
      if (maxDist) {
        url += `?max_distance=${maxDist}`;
      }
      const response = await api.get(url);
      setAvailableRotations(response.data);
    } catch (error) {
      toast.error("Failed to load available rotations");
      setAvailableRotations([]);
    } finally {
      setLoadingRotations(false);
    }
  };

  const handleCreateStudent = async (e) => {
    e.preventDefault();
    setGeocoding(true);
    
    try {
      let latitude = null;
      let longitude = null;
      
      // Geocode if address provided
      if (formData.address && formData.city && formData.state) {
        const addressQuery = `${formData.address}, ${formData.city}, ${formData.state}, ${formData.zip_code}, USA`;
        const geocodeResponse = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressQuery)}&limit=1`
        );
        const geocodeData = await geocodeResponse.json();
        
        if (geocodeData && geocodeData.length > 0) {
          latitude = parseFloat(geocodeData[0].lat);
          longitude = parseFloat(geocodeData[0].lon);
        }
      }
      
      const response = await api.post("/students", {
        ...formData,
        latitude,
        longitude,
      });
      setStudents([...students, response.data]);
      setDialogOpen(false);
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        university: "",
        program: "",
        preferred_specialty: "",
        address: "",
        city: "",
        state: "",
        zip_code: "",
      });
      toast.success("Student added successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add student");
    } finally {
      setGeocoding(false);
    }
  };

  const handleOpenEditDialog = (student) => {
    setEditingStudent(student);
    setEditFormData({
      first_name: student.first_name || "",
      last_name: student.last_name || "",
      email: student.email || "",
      university: student.university || "",
      program: student.program || "",
      address: student.address || "",
      city: student.city || "",
      state: student.state || "",
      zip_code: student.zip_code || "",
      is_hardship: student.is_hardship || false,
      hardship_notes: student.hardship_notes || "",
    });
    setEditDialogOpen(true);
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    if (!editingStudent) return;
    
    setEditGeocoding(true);
    try {
      let latitude = editingStudent.latitude;
      let longitude = editingStudent.longitude;
      
      // Re-geocode if address changed
      const addressChanged = 
        editFormData.address !== editingStudent.address ||
        editFormData.city !== editingStudent.city ||
        editFormData.state !== editingStudent.state;
      
      if (addressChanged && editFormData.address && editFormData.city && editFormData.state) {
        const addressQuery = `${editFormData.address}, ${editFormData.city}, ${editFormData.state}, ${editFormData.zip_code}, USA`;
        const geocodeResponse = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressQuery)}&limit=1`
        );
        const geocodeData = await geocodeResponse.json();
        
        if (geocodeData && geocodeData.length > 0) {
          latitude = parseFloat(geocodeData[0].lat);
          longitude = parseFloat(geocodeData[0].lon);
        }
      }
      
      const response = await api.put(`/students/${editingStudent.id}`, {
        ...editFormData,
        latitude,
        longitude,
      });
      
      setStudents(students.map(s => s.id === editingStudent.id ? response.data : s));
      setEditDialogOpen(false);
      setEditingStudent(null);
      toast.success("Student updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update student");
    } finally {
      setEditGeocoding(false);
    }
  };

  const handleOpenAssignDialog = (student) => {
    setSelectedStudent(student);
    setSelectedSlot("");
    setMaxDistance("");
    setAssignDialogOpen(true);
    fetchRotationsForStudent(student);
  };

  const handleDistanceFilter = () => {
    if (selectedStudent) {
      fetchRotationsForStudent(selectedStudent, maxDistance || null);
    }
  };

  const handleAssignStudent = async () => {
    if (!selectedStudent || !selectedSlot) return;
    try {
      await api.post("/placements", {
        student_id: selectedStudent.id,
        slot_id: selectedSlot,
      });
      setStudents(students.map(s => 
        s.id === selectedStudent.id ? { ...s, placement_status: "pending" } : s
      ));
      setAssignDialogOpen(false);
      setSelectedStudent(null);
      setSelectedSlot("");
      setAvailableRotations([]);
      toast.success("Student assigned to rotation! Awaiting approval.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign student");
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!confirm("Are you sure you want to delete this student?")) return;
    try {
      await api.delete(`/students/${studentId}`);
      setStudents(students.filter(s => s.id !== studentId));
      toast.success("Student deleted");
    } catch (error) {
      toast.error("Failed to delete student");
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return <Badge className="bg-slate-500/10 text-slate-400 border-slate-500/20">Unassigned</Badge>;
    
    // Handle request statuses (request_pending, request_countered, request_accepted)
    if (status.startsWith("request_")) {
      const requestStatus = status.replace("request_", "");
      if (requestStatus === "pending") {
        return <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">Request Pending</Badge>;
      }
      if (requestStatus === "countered") {
        return <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">Counter Offer</Badge>;
      }
      if (requestStatus === "accepted") {
        return <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Accepted</Badge>;
      }
    }
    
    // Handle rejected status
    if (status === "rejected") {
      return <Badge className="bg-red-500/10 text-red-400 border-red-500/20">Rejected</Badge>;
    }
    
    const styles = {
      pending: "status-pending",
      accepted: "status-accepted",
      confirmed: "status-confirmed",
      rejected: "status-rejected",
    };
    return <Badge className={styles[status]}>{status}</Badge>;
  };

  const filteredStudents = students.filter((student) => {
    // Text search filter - search by first name, last name, email, university
    const fullName = `${student.first_name || ''} ${student.last_name || ''}`.toLowerCase();
    const matchesSearch = 
      fullName.includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.university.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    let matchesStatus = true;
    if (statusFilter === "unassigned") {
      matchesStatus = !student.placement_status;
    } else if (statusFilter === "pending") {
      // Include both placement pending and request pending
      matchesStatus = student.placement_status === "pending" || student.placement_status?.startsWith("request_");
    } else if (statusFilter === "confirmed") {
      matchesStatus = student.placement_status === "confirmed";
    }
    
    // Hardship filter
    let matchesHardship = true;
    if (hardshipFilter === "hardship") {
      matchesHardship = student.is_hardship === true;
    } else if (hardshipFilter === "non-hardship") {
      matchesHardship = !student.is_hardship;
    }
    
    return matchesSearch && matchesStatus && matchesHardship;
  });
  
  // Count hardship students
  const hardshipCount = students.filter(s => s.is_hardship).length;
  const hardshipUnassignedCount = students.filter(s => s.is_hardship && !s.placement_status).length;

  const canManageStudents = user?.role === "admin" || user?.role === "university";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="students-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">Students</h1>
          <p className="text-slate-400 mt-1">
            Manage student internship assignments
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ExportDropdown 
            endpoint="/export/students" 
            filename="students"
          />
          {canManageStudents && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-cyan-400 text-black hover:bg-cyan-300 font-semibold shadow-[0_0_15px_rgba(34,211,238,0.3)]"
                  data-testid="add-student-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Student
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-[#151A23] border-slate-700 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-slate-100">Add New Student</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateStudent} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">First Name</Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      placeholder="John"
                      data-testid="student-first-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Last Name</Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      placeholder="Doe"
                      data-testid="student-last-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-slate-800/50 border-slate-700 text-slate-200"
                    placeholder="student@university.edu"
                    data-testid="student-email-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">University</Label>
                  <Input
                    value={formData.university}
                    onChange={(e) => setFormData({ ...formData, university: e.target.value })}
                    required
                    className="bg-slate-800/50 border-slate-700 text-slate-200"
                    placeholder="State University"
                    data-testid="student-university-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Program</Label>
                  <Select onValueChange={(value) => setFormData({ ...formData, program: value })}>
                    <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="student-program-select">
                      <SelectValue placeholder="Select program" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#151A23] border-slate-700">
                      {PROGRAMS.map((prog) => (
                        <SelectItem key={prog} value={prog} className="text-slate-200 focus:bg-slate-800">
                          {prog}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Graduation Date */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300">Graduation Month</Label>
                    <Select onValueChange={(value) => setFormData({ ...formData, graduation_month: value })}>
                      <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="student-grad-month-select">
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#151A23] border-slate-700">
                        <SelectItem value="April" className="text-slate-200 focus:bg-slate-800">April</SelectItem>
                        <SelectItem value="August" className="text-slate-200 focus:bg-slate-800">August</SelectItem>
                        <SelectItem value="December" className="text-slate-200 focus:bg-slate-800">December</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Graduation Year</Label>
                    <Select onValueChange={(value) => setFormData({ ...formData, graduation_year: value })}>
                      <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="student-grad-year-select">
                        <SelectValue placeholder="Select year" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#151A23] border-slate-700">
                        <SelectItem value="2027" className="text-slate-200 focus:bg-slate-800">2027</SelectItem>
                        <SelectItem value="2028" className="text-slate-200 focus:bg-slate-800">2028</SelectItem>
                        <SelectItem value="2029" className="text-slate-200 focus:bg-slate-800">2029</SelectItem>
                        <SelectItem value="2030" className="text-slate-200 focus:bg-slate-800">2030</SelectItem>
                        <SelectItem value="2031" className="text-slate-200 focus:bg-slate-800">2031</SelectItem>
                        <SelectItem value="2032" className="text-slate-200 focus:bg-slate-800">2032</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Home Address Section */}
                <div className="pt-4 border-t border-slate-700">
                  <p className="text-slate-300 font-medium mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-cyan-400" />
                    Home Address (for distance matching)
                  </p>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-slate-400 text-sm">Street Address</Label>
                      <Input
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="bg-slate-800/50 border-slate-700 text-slate-200"
                        placeholder="123 Main St"
                        data-testid="student-address-input"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-slate-400 text-sm">City</Label>
                        <Input
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          className="bg-slate-800/50 border-slate-700 text-slate-200"
                          placeholder="Los Angeles"
                          data-testid="student-city-input"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-400 text-sm">State</Label>
                        <SearchableSelect
                          options={US_STATES.map(state => ({ value: state, label: state }))}
                          value={formData.state}
                          onValueChange={(value) => setFormData({ ...formData, state: value })}
                          placeholder="Select state..."
                          searchPlaceholder="Type state..."
                          emptyText="No matching states."
                        />
                      </div>
                      <div>
                        <Label className="text-slate-400 text-sm">ZIP</Label>
                        <Input
                          value={formData.zip_code}
                          onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                          className="bg-slate-800/50 border-slate-700 text-slate-200"
                          placeholder="90001"
                          data-testid="student-zip-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Hardship Section */}
                <div className="border-t border-slate-700 pt-4 mt-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="is_hardship"
                      checked={formData.is_hardship}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_hardship: checked })}
                      className="mt-1 border-orange-400 data-[state=checked]:bg-orange-400 data-[state=checked]:text-black"
                    />
                    <div className="flex-1">
                      <Label htmlFor="is_hardship" className="text-orange-400 font-medium flex items-center gap-2 cursor-pointer">
                        <Heart className="w-4 h-4" />
                        Hardship Student - Requires Priority Placement
                      </Label>
                      <p className="text-slate-500 text-xs mt-1">
                        Mark this student for priority placement due to special circumstances
                      </p>
                      {formData.is_hardship && (
                        <div className="mt-3">
                          <Label className="text-slate-400 text-sm">Hardship Notes (optional)</Label>
                          <Textarea
                            value={formData.hardship_notes}
                            onChange={(e) => setFormData({ ...formData, hardship_notes: e.target.value })}
                            className="bg-slate-800/50 border-slate-700 text-slate-200 mt-1"
                            placeholder="Describe the special accommodations needed..."
                            rows={2}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-300">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-cyan-400 text-black hover:bg-cyan-300" disabled={geocoding} data-testid="submit-student-btn">
                    {geocoding ? "Locating..." : "Add Student"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <Input
          placeholder="Search students..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-slate-800/50 border-slate-700 text-slate-200"
          data-testid="search-students-input"
        />
      </div>

      {/* My Students Toggle - Only for university users */}
      {user?.role === "university" && (
        <div className="flex items-center gap-4 p-4 bg-[#151A23] rounded-lg border border-slate-700">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMyStudentsFilter(false)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                !myStudentsFilter 
                  ? "bg-cyan-400 text-black" 
                  : "bg-slate-800 text-slate-400 hover:text-slate-200"
              }`}
              data-testid="filter-all-students"
            >
              All Students
            </button>
            <button
              onClick={() => setMyStudentsFilter(true)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                myStudentsFilter 
                  ? "bg-cyan-400 text-black" 
                  : "bg-slate-800 text-slate-400 hover:text-slate-200"
              }`}
              data-testid="filter-my-students"
            >
              My Students
            </button>
          </div>
          <p className="text-slate-500 text-sm">
            {myStudentsFilter ? "Showing only students assigned to you" : "Showing all students in your university"}
          </p>
        </div>
      )}

      {/* Stats - Clickable Filter Cards */}
      <div className={`grid grid-cols-2 gap-3 sm:gap-4 ${user?.role === "clinical_site" ? "md:grid-cols-3 lg:grid-cols-4" : "md:grid-cols-3 lg:grid-cols-5"}`}>
        <Card 
          className={`bg-[#151A23] border-slate-700 cursor-pointer transition-all hover:border-purple-400/50 ${
            statusFilter === "" && hardshipFilter === "" ? "ring-2 ring-purple-400/50 border-purple-400/50" : ""
          }`}
          onClick={() => { setStatusFilter(""); setHardshipFilter(""); }}
          data-testid="filter-total-students"
        >
          <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-400/10 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-slate-400 text-xs sm:text-sm">{user?.role === "clinical_site" ? "Assigned" : "Total"}</p>
              <p className="text-xl sm:text-2xl font-bold text-slate-100">{students.length}</p>
            </div>
          </CardContent>
        </Card>
        
        {/* Hardship Priority Card - only visible to admin and university users */}
        {user?.role !== "clinical_site" && (
          <Card 
            className={`bg-[#151A23] border-slate-700 cursor-pointer transition-all hover:border-orange-400/50 ${
              hardshipFilter === "hardship" ? "ring-2 ring-orange-400/50 border-orange-400/50" : ""
            }`}
            onClick={() => { setHardshipFilter(hardshipFilter === "hardship" ? "" : "hardship"); setStatusFilter(""); }}
            data-testid="filter-hardship"
          >
            <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-orange-400/10 rounded-lg flex items-center justify-center relative">
                <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
                {hardshipUnassignedCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold">
                    {hardshipUnassignedCount}
                  </span>
                )}
              </div>
              <div>
                <p className="text-orange-400 text-xs sm:text-sm font-medium">Hardship</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-100">{hardshipCount}</p>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Unassigned card - only visible to admin and university users */}
        {user?.role !== "clinical_site" && (
          <Card 
            className={`bg-[#151A23] border-slate-700 cursor-pointer transition-all hover:border-slate-400/50 ${
              statusFilter === "unassigned" ? "ring-2 ring-slate-400/50 border-slate-400/50" : ""
            }`}
            onClick={() => { setStatusFilter("unassigned"); setHardshipFilter(""); }}
            data-testid="filter-unassigned"
          >
            <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-slate-400/10 rounded-lg flex items-center justify-center">
                <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs sm:text-sm">Unassigned</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-100">
                  {students.filter(s => !s.placement_status).length}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        <Card 
          className={`bg-[#151A23] border-slate-700 cursor-pointer transition-all hover:border-yellow-400/50 ${
            statusFilter === "pending" ? "ring-2 ring-yellow-400/50 border-yellow-400/50" : ""
          }`}
          onClick={() => { setStatusFilter("pending"); setHardshipFilter(""); }}
          data-testid="filter-pending"
        >
          <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-yellow-400/10 rounded-lg flex items-center justify-center">
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-slate-400 text-xs sm:text-sm">Pending</p>
              <p className="text-xl sm:text-2xl font-bold text-slate-100">
                {students.filter(s => s.placement_status === "pending" || s.placement_status?.startsWith("request_")).length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`bg-[#151A23] border-slate-700 cursor-pointer transition-all hover:border-blue-400/50 ${
            statusFilter === "confirmed" ? "ring-2 ring-blue-400/50 border-blue-400/50" : ""
          }`}
          onClick={() => { setStatusFilter("confirmed"); setHardshipFilter(""); }}
          data-testid="filter-confirmed"
        >
          <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-400/10 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-slate-400 text-xs sm:text-sm">Confirmed</p>
              <p className="text-xl sm:text-2xl font-bold text-slate-100">
                {students.filter(s => s.placement_status === "confirmed").length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Students List */}
      {filteredStudents.length === 0 ? (
        <Card className="bg-[#151A23] border-slate-700">
          <CardContent className="py-16 text-center">
            <GraduationCap className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-300 mb-2">No students found</h3>
            <p className="text-slate-500">
              {searchTerm || statusFilter 
                ? "Try adjusting your search or filter" 
                : "Add your first student to get started"}
            </p>
            {statusFilter && (
              <Button 
                variant="ghost" 
                className="mt-4 text-cyan-400 hover:text-cyan-300"
                onClick={() => setStatusFilter("")}
                data-testid="clear-filter-btn"
              >
                Clear Filter
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-[#151A23] border-slate-700" data-testid="students-table">
          <CardContent className="p-0">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Student</th>
                  <th>Location</th>
                  <th>Program</th>
                  <th>Assigned To</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student) => (
                  <tr key={student.id} className={student.is_hardship ? "bg-orange-500/5" : ""} data-testid={`student-row-${student.id}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded">
                          {student.short_id || student.id.substring(0, 8)}
                        </span>
                        {student.is_hardship && (
                          <span className="flex items-center gap-1 text-orange-400" title={student.hardship_notes || "Hardship student - requires priority placement"}>
                            <Heart className="w-4 h-4 fill-orange-400" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${student.is_hardship ? "bg-orange-400/10" : "bg-purple-400/10"}`}>
                          <GraduationCap className={`w-5 h-5 ${student.is_hardship ? "text-orange-400" : "text-purple-400"}`} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-200 flex items-center gap-2">
                            {student.first_name} {student.last_name}
                            {student.is_hardship && (
                              <Badge className="bg-orange-500/20 text-orange-400 border-orange-400/20 text-[10px] px-1.5 py-0">
                                PRIORITY
                              </Badge>
                            )}
                          </p>
                          <p className="text-slate-500 text-sm">{student.university}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      {student.city ? (
                        <span className="text-slate-300 flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-cyan-400" />
                          {student.city}, {student.state}
                        </span>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td>
                      <Badge className="bg-cyan-400/10 text-cyan-400 border-cyan-400/20">
                        {student.program}
                      </Badge>
                    </td>
                    <td>
                      {student.assigned_to ? (
                        <div className="flex items-center gap-2">
                          <UserCheck className="w-4 h-4 text-emerald-400" />
                          <span className="text-emerald-400 text-sm">
                            {student.assigned_to === user?.id ? "You" : student.assigned_to_name || "Assigned"}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-500 text-sm">Unassigned</span>
                      )}
                    </td>
                    <td>{getStatusBadge(student.placement_status)}</td>
                    <td>
                      <div className="flex gap-1">
                        {/* Assign/Unassign to me button - Only for university users */}
                        {user?.role === "university" && (
                          student.assigned_to === user?.id ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUnassignFromMe(student.id)}
                              className="text-orange-400 hover:text-orange-300"
                              title="Remove from my students"
                              data-testid={`unassign-from-me-${student.id}`}
                            >
                              <UserMinus className="w-4 h-4" />
                            </Button>
                          ) : !student.assigned_to && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAssignToMe(student.id)}
                              className="text-emerald-400 hover:text-emerald-300"
                              title="Add to my students"
                              data-testid={`assign-to-me-${student.id}`}
                            >
                              <UserCheck className="w-4 h-4" />
                            </Button>
                          )
                        )}
                        {canManageStudents && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/students/${student.short_id || student.id}/matches`)}
                            className="text-yellow-400 hover:text-yellow-300"
                            title="Smart Match"
                            data-testid={`find-matches-${student.id}`}
                          >
                            <Target className="w-4 h-4" />
                          </Button>
                        )}
                        {canManageStudents && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEditDialog(student)}
                            className="text-cyan-400 hover:text-cyan-300"
                            data-testid={`edit-student-${student.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        {canManageStudents && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteStudent(student.id)}
                            className="text-red-400 hover:text-red-300"
                            data-testid={`delete-student-${student.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-[#151A23] border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Find Rotation for Student</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Student Info */}
            <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-400/10 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-slate-100 font-medium">{selectedStudent?.name}</p>
                    <p className="text-slate-400 text-sm">{selectedStudent?.program}</p>
                    <p className="text-slate-500 text-sm">{selectedStudent?.university}</p>
                  </div>
                </div>
                {selectedStudent?.city && (
                  <div className="text-right">
                    <p className="text-slate-400 text-xs uppercase">Home Location</p>
                    <p className="text-slate-300 text-sm flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {selectedStudent.city}, {selectedStudent.state}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Distance Filter */}
            <div className="flex items-end gap-3 p-3 bg-slate-800/20 rounded-lg">
              <div className="flex-1">
                <Label className="text-slate-400 text-sm">Maximum Distance (miles)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 50"
                  value={maxDistance}
                  onChange={(e) => setMaxDistance(e.target.value)}
                  className="bg-slate-800/50 border-slate-700 text-slate-200 mt-1"
                  data-testid="max-distance-input"
                />
              </div>
              <Button 
                onClick={handleDistanceFilter}
                variant="outline"
                className="border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10"
                data-testid="filter-distance-btn"
              >
                <Navigation className="w-4 h-4 mr-2" />
                Filter
              </Button>
            </div>

            {/* Available Rotations */}
            <div>
              <Label className="text-slate-300 mb-2 block">
                Available Rotations 
                {availableRotations.length > 0 && (
                  <span className="text-slate-500 ml-2">({availableRotations.length} found)</span>
                )}
              </Label>
              
              {loadingRotations ? (
                <div className="flex items-center justify-center py-8">
                  <div className="spinner w-8 h-8"></div>
                </div>
              ) : availableRotations.length === 0 ? (
                <div className="text-center py-8 bg-slate-800/30 rounded-lg">
                  <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-400">No rotations found</p>
                  <p className="text-slate-500 text-sm">Try adjusting the distance filter</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {availableRotations.map((rotation) => (
                    <div
                      key={rotation.id}
                      onClick={() => setSelectedSlot(rotation.id)}
                      className={`p-4 rounded-lg cursor-pointer transition-all border ${
                        selectedSlot === rotation.id
                          ? "bg-cyan-400/10 border-cyan-400/50"
                          : "bg-slate-800/30 border-slate-700 hover:border-slate-600"
                      }`}
                      data-testid={`rotation-option-${rotation.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Date Range */}
                          <p className="text-cyan-400 font-mono text-sm font-semibold">
                            {rotation.start_date} - {rotation.end_date}
                          </p>
                          {/* Clinical Site */}
                          <p className="text-slate-200 font-medium mt-1">{rotation.clinical_site_name}</p>
                          <p className="text-slate-500 text-sm">{rotation.clinical_site_city}, {rotation.clinical_site_state}</p>
                          {/* Specialty & Program Match */}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className="bg-cyan-400/10 text-cyan-400 border-cyan-400/20 text-xs">
                              {rotation.specialty}
                            </Badge>
                            {rotation.program_match && (
                              <Badge className="bg-green-400/10 text-green-400 border-green-400/20 text-xs">
                                Program Match
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          {/* Distance */}
                          {rotation.distance_miles !== null ? (
                            <div className="flex items-center gap-1 text-slate-300">
                              <Navigation className="w-4 h-4 text-cyan-400" />
                              <span className="font-semibold">{rotation.distance_miles}</span>
                              <span className="text-slate-500 text-sm">mi</span>
                            </div>
                          ) : (
                            <span className="text-slate-500 text-sm">Distance N/A</span>
                          )}
                          {/* Slots */}
                          <p className="text-slate-400 text-sm mt-1">
                            {rotation.available_slots} / {rotation.capacity} slots
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
              <p className="text-yellow-400 text-sm">
                <strong>Note:</strong> This request will be sent to the clinical site for approval. 
                The student will be assigned 1 slot for the full rotation period once approved.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => setAssignDialogOpen(false)} 
                className="text-slate-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAssignStudent}
                disabled={!selectedSlot}
                className="bg-cyan-400 text-black hover:bg-cyan-300"
                data-testid="confirm-assign-btn"
              >
                Submit Slot Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Student Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#151A23] border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Edit Student</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateStudent} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">First Name</Label>
                <Input
                  value={editFormData.first_name}
                  onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
                  required
                  className="bg-slate-800/50 border-slate-700 text-slate-200"
                  data-testid="edit-student-first-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Last Name</Label>
                <Input
                  value={editFormData.last_name}
                  onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
                  required
                  className="bg-slate-800/50 border-slate-700 text-slate-200"
                  data-testid="edit-student-last-name-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                required
                className="bg-slate-800/50 border-slate-700 text-slate-200"
                data-testid="edit-student-email-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">University</Label>
              <Input
                value={editFormData.university}
                onChange={(e) => setEditFormData({ ...editFormData, university: e.target.value })}
                required
                className="bg-slate-800/50 border-slate-700 text-slate-200"
                data-testid="edit-student-university-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Program</Label>
                <Select value={editFormData.program} onValueChange={(value) => setEditFormData({ ...editFormData, program: value })}>
                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="edit-student-program-select">
                    <SelectValue placeholder="Select program" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151A23] border-slate-700">
                    {PROGRAMS.map((prog) => (
                      <SelectItem key={prog} value={prog} className="text-slate-200 focus:bg-slate-800">
                        {prog}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Home Address Section */}
            <div className="pt-4 border-t border-slate-700">
              <p className="text-slate-300 font-medium mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-cyan-400" />
                Home Address (for distance matching)
              </p>
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-400 text-sm">Street Address</Label>
                  <Input
                    value={editFormData.address}
                    onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                    className="bg-slate-800/50 border-slate-700 text-slate-200"
                    placeholder="123 Main St"
                    data-testid="edit-student-address-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-slate-400 text-sm">City</Label>
                    <Input
                      value={editFormData.city}
                      onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      placeholder="Los Angeles"
                      data-testid="edit-student-city-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">State</Label>
                    <SearchableSelect
                      options={US_STATES.map(state => ({ value: state, label: state }))}
                      value={editFormData.state}
                      onValueChange={(value) => setEditFormData({ ...editFormData, state: value })}
                      placeholder="Select state..."
                      searchPlaceholder="Type state..."
                      emptyText="No matching states."
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">ZIP</Label>
                    <Input
                      value={editFormData.zip_code}
                      onChange={(e) => setEditFormData({ ...editFormData, zip_code: e.target.value })}
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      placeholder="90001"
                      data-testid="edit-student-zip-input"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Hardship Section */}
            <div className="border-t border-slate-700 pt-4 mt-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="edit_is_hardship"
                  checked={editFormData.is_hardship}
                  onCheckedChange={(checked) => setEditFormData({ ...editFormData, is_hardship: checked })}
                  className="mt-1 border-orange-400 data-[state=checked]:bg-orange-400 data-[state=checked]:text-black"
                />
                <div className="flex-1">
                  <Label htmlFor="edit_is_hardship" className="text-orange-400 font-medium flex items-center gap-2 cursor-pointer">
                    <Heart className="w-4 h-4" />
                    Hardship Student - Requires Priority Placement
                  </Label>
                  <p className="text-slate-500 text-xs mt-1">
                    Mark this student for priority placement due to special circumstances
                  </p>
                  {editFormData.is_hardship && (
                    <div className="mt-3">
                      <Label className="text-slate-400 text-sm">Hardship Notes (optional)</Label>
                      <Textarea
                        value={editFormData.hardship_notes}
                        onChange={(e) => setEditFormData({ ...editFormData, hardship_notes: e.target.value })}
                        className="bg-slate-800/50 border-slate-700 text-slate-200 mt-1"
                        placeholder="Describe the special accommodations needed..."
                        rows={2}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setEditDialogOpen(false)} className="text-slate-300">
                Cancel
              </Button>
              <Button type="submit" className="bg-cyan-400 text-black hover:bg-cyan-300" disabled={editGeocoding} data-testid="save-student-btn">
                {editGeocoding ? "Saving..." : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
